import {
  APIComponentAttributes,
  APIFieldAttributes,
  APIFieldType,
  APIReferenceAttributes,
  ArdoqId,
  type DateDistribution,
  type Distribution,
  type EnumDistribution,
  FieldSummary,
  FormattingOperator,
  type NumericDistribution,
  type TextDistribution,
  TypeSummary,
  VisibleDataSummary,
} from '@ardoq/api-types';
import {
  EnhancedScopeData,
  LoadedGraphWithViewpointMode,
} from '@ardoq/data-model';
import { enhancedScopeDataOperations, fieldUtils } from '@ardoq/scope-data';
import { dateRangeOperations } from '@ardoq/date-range';
import {
  COMPONENT_BUILT_IN_FIELD_DEFS,
  extendWithCombinedDateRangeFields,
  getFormattingComparatorsByFieldName,
  getFormattingComparatorsForBuiltInField,
  REFERENCE_BUILT_IN_FIELD_DEFS,
  type BuiltInFieldDef,
} from '@ardoq/perspectives';
import { QueryBuilderFilterType } from '@ardoq/query-builder';

type SummaryField = {
  name: string;
  type: APIFieldType;
  allowedComparators: FormattingOperator[];
};

const filterTypeToApiFieldType = (
  filterType: QueryBuilderFilterType
): APIFieldType => {
  switch (filterType) {
    case QueryBuilderFilterType.NUMBER:
      return APIFieldType.NUMBER;
    case QueryBuilderFilterType.DATE_TIME:
    case QueryBuilderFilterType.DATE:
      return APIFieldType.DATE_TIME;
    case QueryBuilderFilterType.DATE_RANGE:
      return APIFieldType.DATE_TIME_RANGE;
    case QueryBuilderFilterType.SELECT:
      return APIFieldType.LIST;
    case QueryBuilderFilterType.BOOLEAN:
      return APIFieldType.CHECKBOX;
    case QueryBuilderFilterType.SELECT_MULTIPLE:
      return APIFieldType.SELECT_MULTIPLE_LIST;
    default:
      return APIFieldType.TEXT;
  }
};

const toSummaryField = (def: BuiltInFieldDef): SummaryField => ({
  name: def.id,
  type: filterTypeToApiFieldType(def.filterType),
  allowedComparators: getFormattingComparatorsForBuiltInField(def),
});

const COMPONENT_BUILTIN_FIELDS: SummaryField[] =
  COMPONENT_BUILT_IN_FIELD_DEFS.filter(d => d.id !== 'type').map(
    toSummaryField
  );
const REFERENCE_BUILTIN_FIELDS: SummaryField[] =
  REFERENCE_BUILT_IN_FIELD_DEFS.filter(d => d.id !== 'type').map(
    toSummaryField
  );

const OPAQUE_BUILTIN_FIELD_NAMES = new Set([
  '_id',
  'parent',
  'source',
  'target',
  'targetWorkspace',
  'rootWorkspace',
]);

const MAX_TOP_VALUES = 20;
const MAX_BYTES = 256 * 1024;
const LARGE_GRAPH_THRESHOLD = 50_000;
const ENUM_VALUE_TRUNCATE_LEN = 80;

type EntityBucket<T> = {
  typeName: string;
  entities: T[];
};

const isPresent = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== '';

const truncateEnumValue = (value: string): string =>
  value.length > ENUM_VALUE_TRUNCATE_LEN
    ? `${value.slice(0, ENUM_VALUE_TRUNCATE_LEN)}…`
    : value;

const buildEnumDistribution = (
  values: string[],
  topN: number
): EnumDistribution => {
  const counts = new Map<string, number>();
  for (const v of values) {
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  return {
    kind: 'enum',
    distinctCount: counts.size,
    topValues: sorted
      .slice(0, topN)
      .map(([value, count]) => ({ value: truncateEnumValue(value), count })),
  };
};

const buildNumericDistribution = (values: number[]): NumericDistribution => {
  // Avoid Math.min/max with spread: V8 stack-overflows above ~65k args.
  const min = values.reduce((a, b) => (b < a ? b : a), Infinity);
  const max = values.reduce((a, b) => (b > a ? b : a), -Infinity);
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  return { kind: 'numeric', min, max, mean };
};

const buildDateDistribution = (values: string[]): DateDistribution => {
  const sorted = [...values].sort();
  return { kind: 'date', min: sorted[0], max: sorted[sorted.length - 1] };
};

const buildTextDistribution = (values: string[]): TextDistribution => {
  const blankCount = values.filter(v => v.trim() === '').length;
  const avgLength =
    values.length > 0
      ? values.reduce((s, v) => s + v.length, 0) / values.length
      : 0;
  return { kind: 'text', avgLength, blankCount };
};

const extractDateValues = (value: unknown): string[] => {
  if (typeof value === 'string') return [value];
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return [obj.start, obj.end]
      .filter((v): v is string | number => v !== null && v !== undefined)
      .map(String);
  }
  return [];
};

const readFieldValue = (
  entity: APIComponentAttributes | APIReferenceAttributes,
  fieldName: string
): unknown => {
  const values = entity as Record<string, unknown>;
  const dateRangeFieldNames =
    dateRangeOperations.maybeExpandDateRangeFieldName(fieldName);
  if (!dateRangeFieldNames) {
    return values[fieldName];
  }

  const [start, end] = dateRangeFieldNames.map(name => values[name]);
  return isPresent(start) || isPresent(end) ? { start, end } : undefined;
};

const summarizeField = (
  entities: Array<APIComponentAttributes | APIReferenceAttributes>,
  field: SummaryField,
  topN: number
): FieldSummary => {
  const rawValues = entities.map(entity => readFieldValue(entity, field.name));
  const presentCount = rawValues.filter(isPresent).length;
  const nullCount = rawValues.length - presentCount;

  const distribution = ((): Distribution => {
    if (presentCount === 0) return { kind: 'opaque' };
    if (OPAQUE_BUILTIN_FIELD_NAMES.has(field.name)) return { kind: 'opaque' };
    switch (field.type) {
      case APIFieldType.LIST:
      case APIFieldType.CHECKBOX: {
        const strings = rawValues.filter(isPresent).map(v => String(v));
        return buildEnumDistribution(strings, topN);
      }
      case APIFieldType.SELECT_MULTIPLE_LIST: {
        const strings = rawValues
          .filter(isPresent)
          .flatMap(v => (Array.isArray(v) ? v : [v]))
          .map(v => String(v));
        return buildEnumDistribution(strings, topN);
      }
      case APIFieldType.NUMBER: {
        const nums = rawValues
          .filter(isPresent)
          .map(v => Number(v))
          .filter(n => !Number.isNaN(n));
        if (nums.length === 0) return { kind: 'opaque' };
        return buildNumericDistribution(nums);
      }
      case APIFieldType.DATE_ONLY:
      case APIFieldType.DATE_TIME:
      case APIFieldType.DATE_ONLY_RANGE:
      case APIFieldType.DATE_TIME_RANGE: {
        const dates = rawValues.filter(isPresent).flatMap(extractDateValues);
        if (dates.length === 0) return { kind: 'opaque' };
        return buildDateDistribution(dates);
      }
      case APIFieldType.TEXT: {
        const strings = rawValues.filter(isPresent).map(v => String(v));
        const distinctCount = new Set(strings).size;
        if (distinctCount <= 50) return buildEnumDistribution(strings, topN);
        return buildTextDistribution(strings);
      }
      case APIFieldType.TEXT_AREA: {
        // TEXT_AREA is always free-form; never promote to enum like TEXT does.
        const strings = rawValues.filter(isPresent).map(v => String(v));
        return buildTextDistribution(strings);
      }
      case APIFieldType.FILE:
      case APIFieldType.URL:
      case APIFieldType.EMAIL:
      case APIFieldType.USER:
      default:
        return { kind: 'opaque' };
    }
  })();

  return {
    name: field.name,
    type: field.type,
    presentCount,
    nullCount,
    distribution,
    allowedComparators: field.allowedComparators,
  };
};

type DecoratedComponent = APIComponentAttributes & {
  incomingRefCount: number;
  outgoingRefCount: number;
};

type RefCountsByComponentId = {
  incoming: Map<ArdoqId, number>;
  outgoing: Map<ArdoqId, number>;
};

const computeRefCountsByComponentId = (
  references: APIReferenceAttributes[]
): RefCountsByComponentId => {
  const incoming = new Map<ArdoqId, number>();
  const outgoing = new Map<ArdoqId, number>();
  for (const reference of references) {
    outgoing.set(reference.source, (outgoing.get(reference.source) ?? 0) + 1);
    incoming.set(reference.target, (incoming.get(reference.target) ?? 0) + 1);
  }
  return { incoming, outgoing };
};

const decorateComponentWithRefCounts = (
  component: APIComponentAttributes,
  refCounts: RefCountsByComponentId
): DecoratedComponent => ({
  ...component,
  incomingRefCount: refCounts.incoming.get(component._id) ?? 0,
  outgoingRefCount: refCounts.outgoing.get(component._id) ?? 0,
});

const bucketComponentsByType = (
  ids: ArdoqId[],
  scopeData: Parameters<typeof enhancedScopeDataOperations.getComponent>[0],
  refCounts: RefCountsByComponentId
): Map<string, EntityBucket<DecoratedComponent>> => {
  const buckets = new Map<string, EntityBucket<DecoratedComponent>>();
  for (const id of ids) {
    const component = enhancedScopeDataOperations.getComponent(scopeData, id);
    if (!component) continue;
    const componentType =
      enhancedScopeDataOperations.getComponentTypeByComponentId(scopeData, id);
    if (!componentType) continue;
    const key = componentType.id;
    if (!buckets.has(key)) {
      buckets.set(key, { typeName: componentType.name, entities: [] });
    }
    buckets
      .get(key)!
      .entities.push(decorateComponentWithRefCounts(component, refCounts));
  }
  return buckets;
};

const getReferencesByIds = (
  scopeData: EnhancedScopeData,
  ids: ArdoqId[]
): APIReferenceAttributes[] =>
  ids
    .map(id => enhancedScopeDataOperations.getReference(scopeData, id))
    .filter((r): r is APIReferenceAttributes => Boolean(r));

const bucketReferencesByType = (
  ids: ArdoqId[],
  scopeData: Parameters<typeof enhancedScopeDataOperations.getReference>[0]
): Map<string, EntityBucket<APIReferenceAttributes>> => {
  const buckets = new Map<string, EntityBucket<APIReferenceAttributes>>();
  for (const id of ids) {
    const reference = enhancedScopeDataOperations.getReference(scopeData, id);
    if (!reference) continue;
    const referenceType =
      enhancedScopeDataOperations.getReferenceTypeByReferenceId(scopeData, id);
    if (!referenceType) continue;
    const key = String(referenceType.id);
    if (!buckets.has(key)) {
      buckets.set(key, { typeName: referenceType.name, entities: [] });
    }
    buckets.get(key)!.entities.push(reference);
  }
  return buckets;
};

const toSummaryFieldsFromCustom = (
  fields: APIFieldAttributes[]
): SummaryField[] => {
  const comparatorsByName = getFormattingComparatorsByFieldName(fields);

  return Object.values(
    extendWithCombinedDateRangeFields(
      Object.fromEntries(fields.map(field => [field.name, field]))
    )
  ).map(({ name, type }) => ({
    name,
    type,
    allowedComparators: comparatorsByName[name] ?? [],
  }));
};

const buildTypeSummary = <
  T extends APIComponentAttributes | APIReferenceAttributes,
>(
  bucket: EntityBucket<T>,
  getFields: (entityId: ArdoqId) => Record<string, APIFieldAttributes>,
  appliesTo: (fieldId: ArdoqId) => boolean,
  builtinFields: SummaryField[],
  topN: number
): TypeSummary => {
  const representativeId = bucket.entities[0]._id;
  const customFields = toSummaryFieldsFromCustom(
    Object.values(getFields(representativeId)).filter(f => appliesTo(f._id))
  );
  const fields = [...customFields, ...builtinFields];
  return {
    typeName: bucket.typeName,
    instanceCount: bucket.entities.length,
    fields: fields.map(field => summarizeField(bucket.entities, field, topN)),
  };
};

const downgradingTextToOpaque = (
  summary: VisibleDataSummary
): VisibleDataSummary => {
  const downgradeFields = (fields: FieldSummary[]): FieldSummary[] =>
    fields.map(f =>
      f.distribution.kind === 'text'
        ? { ...f, distribution: { kind: 'opaque' as const } }
        : f
    );
  const downgradeTypes = (types: TypeSummary[]): TypeSummary[] =>
    types.map(t => ({ ...t, fields: downgradeFields(t.fields) }));
  return {
    componentTypes: downgradeTypes(summary.componentTypes),
    referenceTypes: downgradeTypes(summary.referenceTypes),
  };
};

const trimTopValues = (
  summary: VisibleDataSummary,
  topN: number
): VisibleDataSummary => {
  const trimFields = (fields: FieldSummary[]): FieldSummary[] =>
    fields.map(f => {
      if (f.distribution.kind !== 'enum') return f;
      return {
        ...f,
        distribution: {
          ...f.distribution,
          topValues: f.distribution.topValues.slice(0, topN),
        },
      };
    });
  const trimTypes = (types: TypeSummary[]): TypeSummary[] =>
    types.map(t => ({ ...t, fields: trimFields(t.fields) }));
  return {
    componentTypes: trimTypes(summary.componentTypes),
    referenceTypes: trimTypes(summary.referenceTypes),
  };
};

const byteLength = (summary: VisibleDataSummary): number =>
  new TextEncoder().encode(JSON.stringify(summary)).length;

const enforceSizeBudget = (
  summary: VisibleDataSummary,
  totalEntities: number
): VisibleDataSummary => {
  const isLargeGraph = totalEntities > LARGE_GRAPH_THRESHOLD;
  let result = isLargeGraph ? downgradingTextToOpaque(summary) : summary;

  if (!isLargeGraph && byteLength(result) > MAX_BYTES) {
    result = downgradingTextToOpaque(result);
  }
  if (byteLength(result) > MAX_BYTES) {
    result = trimTopValues(result, 10);
  }
  return result;
};

export const buildVisibleDataSummary = (
  loadedGraph: LoadedGraphWithViewpointMode
): VisibleDataSummary => {
  const { scopeComponentIds, scopeReferenceIds, scopeData } = loadedGraph;

  const visibleReferences = getReferencesByIds(scopeData, scopeReferenceIds);
  const refCounts = computeRefCountsByComponentId(visibleReferences);

  const componentBuckets = bucketComponentsByType(
    scopeComponentIds,
    scopeData,
    refCounts
  );
  const componentTypes: TypeSummary[] = [...componentBuckets.values()].map(
    bucket =>
      buildTypeSummary(
        bucket,
        id => enhancedScopeDataOperations.getComponentFields(scopeData, id),
        fieldId =>
          fieldUtils.appliesToComponent(
            scopeData,
            fieldId,
            bucket.entities[0]._id
          ),
        COMPONENT_BUILTIN_FIELDS,
        MAX_TOP_VALUES
      )
  );

  const referenceBuckets = bucketReferencesByType(scopeReferenceIds, scopeData);
  const referenceTypes: TypeSummary[] = [...referenceBuckets.values()].map(
    bucket =>
      buildTypeSummary(
        bucket,
        id => enhancedScopeDataOperations.getReferenceFields(scopeData, id),
        fieldId =>
          fieldUtils.appliesToReference(
            scopeData,
            fieldId,
            bucket.entities[0]._id
          ),
        REFERENCE_BUILTIN_FIELDS,
        MAX_TOP_VALUES
      )
  );

  const raw: VisibleDataSummary = { componentTypes, referenceTypes };
  return enforceSizeBudget(
    raw,
    scopeComponentIds.length + scopeReferenceIds.length
  );
};
